import { isBefore, subDays } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import type { Cell, Column } from 'react-table';

import type { StaqErrorExtended } from '../../../../apis/graphql';
import { START_OF_DAY_TIME } from '../../../../common/constants';
import { adTagEvents } from '../../../../configs';
import { DEFAULT_TIMEZONE } from '../../../../constants';
import type { AdSequence, Nullable } from '../../../../models';
import { fromEmailToName } from '../../../../utils/common';
import {
  enumToTitleCase,
  formatArray,
  formatDate,
  formatDateTimeToZonedTime,
  formatFloatToPercentage,
  formatMonetaryAmount,
  formatPercentage,
  formatStringDateTime,
  microCurrencyToAmount,
} from '../../../../utils/formatting';
import { startOfDayInUtc } from '../../../../utils/time';
import { NEW_SEQUENCE_LABEL } from '../../../Sequence/constants';
import { POD_POSITION_OPTIONS } from '../../../Sequence/podPositionConstants';
import { SLOT_POSITION_OPTIONS } from '../../../Sequence/slotPositionConstants';
import { UNFORMATTED_PUBLISHERS } from '../../constants';
import {
  ALERT_MESSAGES,
  AlertFiled,
  DEFAULT_FAILURE_CODE_SEPARATOR,
  FAILURE_CODES_TEMPLATE_VARIABLE,
} from '../cells/AlertCell/constants';
import type { LineItemModel } from '../modelConverters';

export type CustomColumnConfig<T extends Object = {}> = Column<T> & {
  isEditable?: boolean;
  doNotLighten?: boolean;
  className?: string;
  disableHiding?: boolean;
  isColumnHiddenByDefault?: boolean;
  tooltipMessage?: string;
  /** Custom accessor for CSV export. If not provided, falls back to accessor. */
  exportAccessor?: (item: T) => string;
  /** If true, this column will be excluded from CSV export. */
  disableForExport?: boolean;
  exportColumns?: { header: string; accessor: (item: T) => string }[];
  headerString?: string;
};

export type CellWithCustomColumnConfig<T extends object = {}> = Cell<T> & {
  column: CustomColumnConfig<T>;
};

export function stringToDateTimeString({ value }: { value: Nullable<string> }): Nullable<string> {
  return value ? formatDate(new Date(value)) : null;
}

export function formatStartEndDateString({ value }: { value: Nullable<string> }): Nullable<string> {
  return value ? formatStringDateTime(value) : null;
}

export function formatMicroCurrencyAmount(value: Nullable<number>, currency?: string): Nullable<string> {
  if (value === null) return null;
  return formatMonetaryAmount(microCurrencyToAmount(value), currency);
}

export function numberToCurrencyAmount(value: Nullable<number>, currency?: string): Nullable<string> {
  return value !== null ? formatMonetaryAmount(value, currency) : null;
}

export function numberToPercentage({ value }: { value: Nullable<number> }): Nullable<string> {
  return value ? formatPercentage(value) : null;
}

export function weightToPercentage({ value }: { value: Nullable<number> }): string {
  return formatFloatToPercentage(value || 0);
}

export function getIdFromTableCell<T extends { id: string }>(cell: Cell<T>): string {
  return cell.row.original.id;
}

export function getPublisherTargetingList({ value }: { value: Nullable<string[]> }): string {
  if (!value || !value.length) return '';

  const publishers = value.filter((publisher) => !!publisher);

  return formatArray(
    publishers
      .map((item) => (item in UNFORMATTED_PUBLISHERS ? item : enumToTitleCase(item)))
      .sort(new Intl.Collator().compare)
  );
}

export function boolToYes({ value, isNoShown }: { value: Nullable<boolean>; isNoShown?: boolean }): Nullable<string> {
  if (value) return 'Yes';

  return isNoShown ? 'No' : null;
}

/**
 * Formats a UTC instant as the wall clock in `timezone`, falling back to Eastern Time when the row
 * carries no timezone of its own.
 */
export function formatUTCToTimezone({
  value,
  timezone,
}: {
  value: Nullable<string>;
  timezone?: Nullable<string>;
}): Nullable<string> {
  return value ? formatDateTimeToZonedTime(value, timezone ?? DEFAULT_TIMEZONE) : null;
}

export const getAdSequenceName = (adSequence?: AdSequence | null): string => {
  if (!adSequence) return '';

  return adSequence.name || NEW_SEQUENCE_LABEL + '1';
};

export const getAccountRelatedColumnAccessor = (value?: string): string => (value ? fromEmailToName(value) : '');

export const getMetricsRelatedColumnAccessor = (value?: Nullable<number>): string =>
  value ? Math.round(value).toLocaleString() : '';

// Unlike getMetricsRelatedColumnAccessor, a 0 is shown: it means the line is fully or over-delivered.
export const getDeliveryRemainingColumnAccessor = (value?: Nullable<number>): string =>
  value == null ? '' : Math.round(value).toLocaleString();

export const getDeliveryRemaining = (li: {
  goal: Nullable<number>;
  billableEvent?: Nullable<string>;
  metrics?: Nullable<{ impressions?: Nullable<number>; completions?: Nullable<number> }>;
}): Nullable<number> => {
  // No goal is absent, matching the null campaign-searcher stores. A fully or over-delivered line is a real 0.
  if (li.goal == null) return null;

  // An absent delivered term is zero delivered, so a goal with no metrics yet keeps its full remainder.
  const actual =
    (li.billableEvent?.toUpperCase() === adTagEvents.START.key ? li.metrics?.impressions : li.metrics?.completions) ??
    0;
  const remaining = li.goal - actual;

  return Math.max(0, remaining);
};

export const getLineItemRemainingExpectedCompletionsAcc = (
  li: LineItemModel,
  adsPacingStatusAlignment?: boolean
): Nullable<number> => {
  if (adsPacingStatusAlignment) {
    return li.metrics?.remainingExpectedCompletionsAcc ?? null;
  }

  return getDeliveryRemaining(li);
};

export const getMetricsPercentRelatedColumnAccessor = (value?: Nullable<number>): number | null =>
  value ? value * 100 : null;

export const getLineItemThreshold = (value: Nullable<number>): string | null =>
  Number.isFinite(value) ? `${value}%` : null;

export const getPercentageValueForExport = (value?: Nullable<number>): string => {
  const percentageValue = getMetricsPercentRelatedColumnAccessor(value);

  return numberToPercentage({ value: percentageValue }) || '';
};

export function getDistributionPartnerList({ value }: { value: Nullable<string[]> }): string {
  if (!value?.length) return '';

  value = value.filter((partners) => !!partners);

  return formatArray(value.map((item) => enumToTitleCase(item)));
}

export const getFailureCodesFromStaqErrorExtended = (
  staqErrorExtended: StaqErrorExtended[],
  lineItemEndDate: Nullable<string> | undefined,
  lineItemTimezone: string
): string[] => {
  const now = new Date();
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const nowStartInUTC = startOfDayInUtc(zonedTimeToUtc(now, currentTimezone));

  let dateToCompare: Date;

  if (!lineItemEndDate) {
    dateToCompare = nowStartInUTC;
  } else {
    const lineItemEndDateInUTC = startOfDayInUtc(zonedTimeToUtc(lineItemEndDate, lineItemTimezone));
    dateToCompare = isBefore(lineItemEndDateInUTC, nowStartInUTC) ? lineItemEndDateInUTC : nowStartInUTC;
  }

  const yesterdayStartFromCompareDate = subDays(dateToCompare, 1);

  return staqErrorExtended.reduce<string[]>((acc, errorExtended) => {
    const errorDateStartInUTC = new Date(`${errorExtended.date}${START_OF_DAY_TIME}`);

    if (
      errorDateStartInUTC.getTime() === dateToCompare.getTime() ||
      errorDateStartInUTC.getTime() === yesterdayStartFromCompareDate.getTime()
    ) {
      acc.push(errorExtended.description);
    }

    return acc;
  }, []);
};

export const getStaqErrorMessage = (
  failureCodes: string[],
  failureCodesSeparator = DEFAULT_FAILURE_CODE_SEPARATOR,
  isFlatString?: boolean
): string => {
  if (failureCodes.length === 0) {
    return '';
  }

  let message = ALERT_MESSAGES[AlertFiled.StaqError] ?? '';

  if (isFlatString) {
    message = message.split(DEFAULT_FAILURE_CODE_SEPARATOR).join(' ').split('\n').join(' ');
  }

  return message?.replace(FAILURE_CODES_TEMPLATE_VARIABLE, failureCodes.join(failureCodesSeparator));
};

export const getStaqErrorFailureCodes = (
  staqError?: Nullable<string[]>,
  staqErrorExtended?: Nullable<StaqErrorExtended[]>,
  lineItemEndDate?: Nullable<string>,
  lineItemTimezone?: Nullable<string>
): string[] => {
  const hasStaqError = staqError && staqError.length > 0;
  const hasStaqErrorExtended = staqErrorExtended && staqErrorExtended.length > 0;

  if (!hasStaqError && !hasStaqErrorExtended) {
    return [];
  }

  let failureCodes: string[] = [];

  if (hasStaqErrorExtended && staqErrorExtended && lineItemTimezone) {
    failureCodes = getFailureCodesFromStaqErrorExtended(staqErrorExtended, lineItemEndDate, lineItemTimezone);
  } else if (hasStaqError && staqError) {
    failureCodes = staqError;
  }

  return failureCodes;
};

export const getPodPositionLabel = (value?: string | null): string => {
  if (!value) return '';
  return POD_POSITION_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
};

export const getSlotPositionLabel = (value?: string | null): string => {
  if (!value) return '';
  return SLOT_POSITION_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
};
